import { c as _c } from "react/compiler-runtime";
/**
 * @file UsersActions.tsx
 * @description
 * -----------------------------------------------------
 * Componente que renderiza las acciones disponibles para usuarios
 * seleccionados (ver, editar, eliminar) según permisos
 * -----------------------------------------------------
 * @version 0.0.1a
 * @created 2025-12-11
 * @author DLTCode
 * @copyright © DLTCode
 * @license Private
 * */

import DeleteOrConfirmModal from "@/components/modals/DeleteOrConfirmModal";
import ButtonComponent from "@/components/ui/ButtonComponent";
import { ColumnsAllUsers } from "@/types/commons/columns";
import { EyeIcon, Lock, LockOpen, RefreshCcw, SquarePen, Trash2Icon } from "lucide-react";
import { useTranslations } from "next-intl";
import { useState, useTransition } from "react";
import { toast } from "sonner";
import { fetchToggleBlockUsers } from "..";
import { DeleteUser, UsersActionsProps } from "../types";
import { useUsersView } from "../users-context";
import EditUserModal from "./edit-user-modal";
import UserDetailsModal from "./user-details-modal";
import ResendInviteModal from "./resend-invite-modal";
import { deleteUser } from "../actions";
const UsersActions: React.FC<UsersActionsProps> = t0 => {
  const $ = _c(60);
  const {
    selectedUsers,
    onClear
  } = t0;
  const [isOpenDetailsModal, setIsOpenDetailsModal] = useState(false);
  const [isOpenEditModal, setIsOpenEditModal] = useState(false);
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const [isOpenBlockModal, setIsOpenBlockModal] = useState(false);
  const [isOpenResendModal, setIsOpenResendModal] = useState(false);
  const {
    getActionPermissions
  } = useUsersView();
  let t1;
  if ($[0] !== getActionPermissions || $[1] !== selectedUsers) {
    t1 = getActionPermissions(selectedUsers);
    $[0] = getActionPermissions;
    $[1] = selectedUsers;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    canView,
    canEdit,
    canDelete,
    canBlock,
    canUnblock
  } = t1;
  let t2;
  if ($[3] !== selectedUsers) {
    t2 = selectedUsers.length > 0 && selectedUsers.every(u => u.status === selectedUsers[0].status);
    $[3] = selectedUsers;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const allSameStatus = t2;
  let t3;
  if ($[5] !== selectedUsers) {
    t3 = selectedUsers.length > 0 && selectedUsers.every(_temp);
    $[5] = selectedUsers;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const isPending = t3;
  let t4;
  if ($[7] !== selectedUsers) {
    t4 = selectedUsers.length > 0 && selectedUsers.every(_temp2);
    $[7] = selectedUsers;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const isBlocked = t4;
  let t5;
  if ($[9] !== selectedUsers) {
    t5 = selectedUsers.length > 0 && selectedUsers.every(_temp3);
    $[9] = selectedUsers;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const isEliminated = t5;
  const [isPendingTransition, startTransition] = useTransition();
  const tableActionsTrans = useTranslations("TableComponent.TableActions");
  let t6;
  if ($[11] !== onClear) {
    t6 = async rows => {
      startTransition(async () => {
        const body = {
          ids: rows.map(_temp4)
        };
        const response = await deleteUser(body);
        if (response.error) {
          toast.error(response.message);
          return;
        }
        toast.success(response.message);
        onClear();
      });
    };
    $[11] = onClear;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const handleDeleteUsers = t6;
  let t7;
  if ($[13] !== onClear || $[14] !== selectedUsers) {
    t7 = async () => {
      startTransition(async () => {
        if (selectedUsers.length === 0) {
          return;
        }
        const status = selectedUsers[0].status;
        const ids = selectedUsers.map(_temp5);
        const action = status === "blocked" ? "unblock" : "block";
        const response_0 = await fetchToggleBlockUsers({
          ids,
          action
        });
        if (response_0.error) {
          toast.error(response_0.message);
          return;
        }
        toast.success(response_0.message);
        onClear();
      });
    };
    $[13] = onClear;
    $[14] = selectedUsers;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const handleBlockUnblock = t7;
  let t8;
  if ($[16] !== isOpenResendModal || $[17] !== isPending || $[18] !== selectedUsers[0]) {
    t8 = isPending && <><ButtonComponent label="buttonResendInvite" color="orange" icon={RefreshCcw} onClick={() => setIsOpenResendModal(true)} /><ResendInviteModal isOpen={isOpenResendModal} onClose={() => setIsOpenResendModal(false)} user={selectedUsers[0]} /></>;
    $[16] = isOpenResendModal;
    $[17] = isPending;
    $[18] = selectedUsers[0];
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== canView || $[21] !== isOpenDetailsModal || $[22] !== isPending || $[23] !== selectedUsers[0]) {
    t9 = !isPending && canView && <><ButtonComponent label="buttonDetails" color="primary" icon={EyeIcon} onClick={() => setIsOpenDetailsModal(true)} /><UserDetailsModal isOpen={isOpenDetailsModal} onClose={() => setIsOpenDetailsModal(false)} user={selectedUsers[0]} /></>;
    $[20] = canView;
    $[21] = isOpenDetailsModal;
    $[22] = isPending;
    $[23] = selectedUsers[0];
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== canEdit || $[26] !== isBlocked || $[27] !== isEliminated || $[28] !== isOpenEditModal || $[29] !== isPending || $[30] !== onClear || $[31] !== selectedUsers[0]) {
    t10 = !isPending && canEdit && !isBlocked && !isEliminated && <><ButtonComponent label="buttonEdit" color="green" icon={SquarePen} onClick={() => setIsOpenEditModal(true)} /><EditUserModal isOpen={isOpenEditModal} user={selectedUsers[0]} onClose={() => setIsOpenEditModal(false)} onClear={onClear} /></>;
    $[25] = canEdit;
    $[26] = isBlocked;
    $[27] = isEliminated;
    $[28] = isOpenEditModal;
    $[29] = isPending;
    $[30] = onClear;
    $[31] = selectedUsers[0];
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[33] !== allSameStatus || $[34] !== canBlock || $[35] !== canUnblock || $[36] !== handleBlockUnblock || $[37] !== isBlocked || $[38] !== isEliminated || $[39] !== isOpenBlockModal || $[40] !== isPending || $[41] !== isPendingTransition || $[42] !== selectedUsers.length || $[43] !== tableActionsTrans) {
    t11 = !isPending && !isEliminated && (canBlock || canUnblock) && allSameStatus && <><ButtonComponent label={isBlocked ? "buttonUnblock" : "buttonBlock"} color={isBlocked ? "green" : "orange"} icon={isBlocked ? LockOpen : Lock} onClick={() => setIsOpenBlockModal(true)} /><DeleteOrConfirmModal title={tableActionsTrans(isBlocked ? "titleConfirmUnblock" : "titleConfirmBlock", {
        count: selectedUsers.length
      })} isOpen={isOpenBlockModal} loading={isPendingTransition} onConfirm={() => handleBlockUnblock()} onClose={() => setIsOpenBlockModal(false)} onReject={() => setIsOpenBlockModal(false)} /></>;
    $[33] = allSameStatus;
    $[34] = canBlock;
    $[35] = canUnblock;
    $[36] = handleBlockUnblock;
    $[37] = isBlocked;
    $[38] = isEliminated;
    $[39] = isOpenBlockModal;
    $[40] = isPending;
    $[41] = isPendingTransition;
    $[42] = selectedUsers.length;
    $[43] = tableActionsTrans;
    $[44] = t11;
  } else {
    t11 = $[44];
  }
  let t12;
  if ($[45] !== canDelete || $[46] !== handleDeleteUsers || $[47] !== isEliminated || $[48] !== isOpenDeleteModal || $[49] !== isPending || $[50] !== isPendingTransition || $[51] !== selectedUsers || $[52] !== tableActionsTrans) {
    t12 = !isEliminated && (canDelete || isPending) && <><ButtonComponent label="buttonDelete" color="red" icon={Trash2Icon} onClick={() => setIsOpenDeleteModal(true)} /><DeleteOrConfirmModal title={tableActionsTrans("titleConfirmDelete", {
        count: selectedUsers.length
      })} isOpen={isOpenDeleteModal} loading={isPendingTransition} onConfirm={() => handleDeleteUsers(selectedUsers)} onClose={() => setIsOpenDeleteModal(false)} onReject={() => setIsOpenDeleteModal(false)} /></>;
    $[45] = canDelete;
    $[46] = handleDeleteUsers;
    $[47] = isEliminated;
    $[48] = isOpenDeleteModal;
    $[49] = isPending;
    $[50] = isPendingTransition;
    $[51] = selectedUsers;
    $[52] = tableActionsTrans;
    $[53] = t12;
  } else {
    t12 = $[53];
  }
  let t13;
  if ($[54] !== t10 || $[55] !== t11 || $[56] !== t12 || $[57] !== t8 || $[58] !== t9) {
    t13 = <>{t8}{t9}{t10}{t11}{t12}</>;
    $[54] = t10;
    $[55] = t11;
    $[56] = t12;
    $[57] = t8;
    $[58] = t9;
    $[59] = t13;
  } else {
    t13 = $[59];
  }
  return t13;
};
export default UsersActions;
function _temp(u_0) {
  return u_0.status === "pending";
}
function _temp2(u_1) {
  return u_1.status === "blocked";
}
function _temp3(u_2) {
  return u_2.status === "eliminated";
}
function _temp4(row) {
  return parseInt(row.id);
}
function _temp5(row_0) {
  return row_0.id;
}